import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { Button, Card, CardFooter, Field, Link, mergeClasses, Text } from '@fluentui/react-components'
import {
    bundleIcon,
    CheckmarkFilled,
    CheckmarkRegular,
    DocumentQueueAddFilled,
    DocumentQueueAddRegular,
    OpenFilled,
    OpenRegular,
    PauseCircleFilled,
    PauseCircleRegular,
    SaveFilled,
    SaveRegular,
    ShiftsActivityFilled,
    ShiftsActivityRegular,
} from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'
import useAxios from '../../hooks/useAxios'
import MoneyText from '../../components/Text/Currency/MoneyText'
import useCurrencies from '../../hooks/useCurrencies'
import { formatFullDate } from '../../helpers/dateHelper'
import { InvoiceApprovalStatusLabels } from '../../components/TagPicker/InvoiceApprovalStatusPicker'
import {
    createInvoiceFromApproval,
    InvoiceApprovalMutationDto,
    useInvoiceApprovalStyles,
    ViewInvoiceApprovalComponentProps,
} from './ViewInvoiceApproval'
import { useHistory } from 'react-router-dom'
import { FilePartyMetaDataDto } from '../../config/finance/invoiceConfig'
import { InvoiceApprovalStatus } from './InvoiceApprovalOverview'
import DeleteDialog from '../../components/Dialog/DeleteDialog'
import { Destination } from '../../context/AxiosContext'
import { parseNumber } from '../../helpers/numberHelper'
import StealthLink from '../../components/StealthLink'
import FileCombobox from '../../components/Combobox/FileCombobox'
import { FileRateType } from '../../context/GlobalContext'
import useViewport, { Viewport } from '../../hooks/useViewport'
import FormikComboboxField from '../../components/Formik/FormikComboboxField'
import FilePartyCombobox from '../../components/Combobox/FilePartyCombobox'
import FormikInputField from '../../components/Formik/FormikInputField'
import { SummaryDto } from '../../config/fileConfig'
import { ExtendedTimeOverviewDto } from '../../v2/pages/Time/BaseTimeOverview'
import { ExtendedDisbursementOverviewDto } from '../Disbursement/BaseDisbursementOverview'
import { ExtendedFixedPriceOverviewDto } from '../FixedPrice/BaseFixedPriceOverview'
import { ExtendedTravelOverviewDto } from '../Travel/BaseTravelOverview'
import useFixedPriceTypes from '../../hooks/useFixedPriceTypes'
import { SubComboboxProps } from '../../components/Combobox/BaseCombobox'

const CheckmarkIcon = bundleIcon(CheckmarkFilled, CheckmarkRegular)
const CreateInvoiceIcon = bundleIcon(DocumentQueueAddFilled, DocumentQueueAddRegular)
const PauseCircleIcon = bundleIcon(PauseCircleFilled, PauseCircleRegular)
const SaveIcon = bundleIcon(SaveFilled, SaveRegular)
const ShiftsActivityIcon = bundleIcon(ShiftsActivityFilled, ShiftsActivityRegular)
const OpenIcon = bundleIcon(OpenFilled, OpenRegular)

export type InvoiceApprovalHeaderProps = ViewInvoiceApprovalComponentProps & {
    headerIsDirty?: boolean
    file?: SummaryDto
    client?: FilePartyMetaDataDto
    debtor?: FilePartyMetaDataDto
    selectedDisbursements: ExtendedDisbursementOverviewDto[]
    selectedFixedPrices: ExtendedFixedPriceOverviewDto[]
    selectedTimes: ExtendedTimeOverviewDto[]
    selectedTravels: ExtendedTravelOverviewDto[]
}

function NewCardFooter(props: InvoiceApprovalHeaderProps) {
    const styles = useInvoiceApprovalStyles()
    const { t: translate } = useTranslation()

    const { submitForm } = useFormikContext<InvoiceApprovalMutationDto>()

    return (
        <CardFooter className={styles.buttonContainer}>
            <div></div>
            <div>
                <Button icon={<SaveIcon />} appearance={'primary'} onClick={submitForm} disabled={!props.headerIsDirty}>
                    {translate('action.save')}
                </Button>
            </div>
        </CardFooter>
    )
}

function EditCardFooter(props: InvoiceApprovalHeaderProps) {
    const axios = useAxios()
    const { getFixedPriceType } = useFixedPriceTypes()
    const history = useHistory()
    const styles = useInvoiceApprovalStyles()
    const { t: translate } = useTranslation()

    const { values } = useFormikContext<InvoiceApprovalMutationDto>()

    return (
        <CardFooter className={props.orientation === 'vertical' ? undefined : styles.buttonContainer}>
            {props.orientation === 'vertical' ? undefined : <div></div>}
            <div className={styles.flexGap}>
                {props.isFileOwnerOrAdmin && (
                    <DeleteDialog
                        disabled={InvoiceApprovalStatus.Done === props.approvalStatus}
                        onDelete={(setOpen, setSubmitting) => {
                            axios
                                .delete(`/finance/invoice-approval/${props.approval.id}`)
                                .then(() => {
                                    setOpen(false)
                                    history.push(`/invoice-approval`)
                                })
                                .catch(() => {
                                    setSubmitting(false)
                                })
                        }}
                    />
                )}
                {![InvoiceApprovalStatus.Done, InvoiceApprovalStatus.Rejected].includes(props.approvalStatus) && props.isFileOwnerOrAdmin && (
                    <Button
                        icon={<PauseCircleIcon />}
                        appearance={'secondary'}
                        onClick={() => {
                            axios.post(`/finance/invoice-approval/${props.approval.id}/rejected`).then(() => {
                                props.setApprovalStatus(InvoiceApprovalStatus.Rejected)
                            })
                        }}
                    >
                        {props.orientation === 'vertical' ? undefined : translate(`invoiceapproval.action.rejected`)}
                    </Button>
                )}
                {[InvoiceApprovalStatus.Rejected, InvoiceApprovalStatus.OwnerApproved].includes(props.approvalStatus) && props.isFileOwnerOrAdmin && (
                    <Button
                        icon={<ShiftsActivityIcon />}
                        appearance={'secondary'}
                        onClick={() => {
                            axios.post(`/finance/invoice-approval/${props.approval.id}/active`).then(() => {
                                props.setApprovalStatus(InvoiceApprovalStatus.Active)
                            })
                        }}
                    >
                        {props.orientation === 'vertical' ? undefined : translate(`invoiceapproval.action.active`)}
                    </Button>
                )}
                {[InvoiceApprovalStatus.New, InvoiceApprovalStatus.Active, InvoiceApprovalStatus.Approved].includes(props.approvalStatus) &&
                    props.isFileOwnerOrAdmin && (
                        <Button
                            icon={<CheckmarkIcon />}
                            appearance={'secondary'}
                            onClick={() => {
                                axios.post(`/finance/invoice-approval/${props.approval.id}/ownerapproved`).then(() => {
                                    props.setApprovalStatus(InvoiceApprovalStatus.OwnerApproved)
                                    history.push(`/invoice-approval`)
                                })
                            }}
                        >
                            {props.orientation === 'vertical' ? undefined : translate(`invoiceapproval.action.ownerapproved`)}
                        </Button>
                    )}
                {props.isFileOwnerOrAdminOrInvoicer && (
                    <Button
                        icon={<CreateInvoiceIcon />}
                        disabled={
                            [InvoiceApprovalStatus.Done, InvoiceApprovalStatus.Rejected].includes(props.approvalStatus) ||
                            !values.debtorId ||
                            !values.clientId ||
                            props.headerIsDirty
                        }
                        onClick={() =>
                            history.push(
                                `/invoice/add`,
                                createInvoiceFromApproval(
                                    values,
                                    props.approval,
                                    props.invoiceTemplate,
                                    getFixedPriceType,
                                    props.selectedFixedPrices,
                                ),
                            )
                        }
                    >
                        {props.orientation === 'vertical' ? undefined : translate('invoiceapproval.action.create')}
                    </Button>
                )}
            </div>
        </CardFooter>
    )
}

export default function InvoiceApprovalHeader(props: InvoiceApprovalHeaderProps): ReactElement {
    const { getCurrency, getCurrencyName } = useCurrencies()
    const history = useHistory()
    const styles = useInvoiceApprovalStyles()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const isDualColumn = useMemo<boolean>(() => isViewport('>=', Viewport.XL), [viewport])

    const { initialValues, values, setFieldValue } = useFormikContext<InvoiceApprovalMutationDto>()
    const headerIsDirty = useMemo(() => {
        return (
            initialValues.clientId !== values.clientId ||
            initialValues.debtorId !== values.debtorId ||
            initialValues.invoiceEmail !== values.invoiceEmail ||
            initialValues.invoiceReference !== values.invoiceReference
        )
    }, [values.clientId, values.debtorId, values.invoiceEmail, values.invoiceReference])

    //Update the total rate when (de)selecting times.
    const totalDisbursementRate = useMemo<number>(
        () => props.selectedDisbursements.map(item => parseNumber(item.rate.amount, 'en')).reduce((a, b) => a + b, 0),
        [props.selectedDisbursements],
    )
    const totalFixedPriceRate = useMemo<number>(
        () => props.selectedFixedPrices.map(item => parseNumber(item.rate.amount, 'en')).reduce((a, b) => a + b, 0),
        [props.selectedFixedPrices],
    )
    const totalTimeRate = useMemo<number>(
        () => props.selectedTimes.map(item => parseNumber(item.turnover.amount, 'en')).reduce((a, b) => a + b, 0),
        [props.selectedTimes],
    )
    const totalTravelRate = useMemo<number>(
        () => props.selectedTravels.map(item => parseNumber(item.turnover.amount, 'en')).reduce((a, b) => a + b, 0),
        [props.selectedTravels],
    )

    return (
        <Card className={styles.cardMargin}>
            <div className={mergeClasses(styles.cardSingleColumnGrid, isDualColumn && styles.cardDualColumnGrid)}>
                <div>
                    {!values.id && (
                        <Field label={translate('invoiceapproval.fileids.label')} orientation={props.headerOrientation} required>
                            <FileCombobox
                                autoFocus
                                disabled={!!values.id}
                                selectedOptions={values.fileIds}
                                onOptionSelect={(_, data) => {
                                    setFieldValue('fileIds', data.selectedOptions)
                                }}
                            />
                        </Field>
                    )}
                    {values.id && (
                        <Field label={translate('invoiceapproval.fileids.label')} orientation={props.headerOrientation}>
                            <Text>{props.meta.data?.files?.map(file => file.reference + ' - ' + file.name).join(', ')}</Text>
                        </Field>
                    )}
                    {props.meta.loaded && (
                        <>
                            <Field label={translate('invoiceapproval.userid')} orientation={props.headerOrientation}>
                                <Text>{props.meta.data?.files?.[0].owner.fullName}</Text>
                            </Field>
                            {props.approval && (
                                <>
                                    <Field label={translate('invoiceapproval.date')} orientation={props.headerOrientation}>
                                        <Text>{formatFullDate(props.approval?.date)}</Text>
                                    </Field>
                                    <Field label={translate('invoiceapproval.status.label')} orientation={props.headerOrientation}>
                                        <Text>{translate(InvoiceApprovalStatusLabels.get(props.approvalStatus)?.name)}</Text>
                                    </Field>
                                </>
                            )}
                            <Field label={translate('invoiceapproval.fileids.rate')} orientation={props.headerOrientation}>
                                {props.meta.data?.files[0]?.fileRateType === FileRateType.File ? (
                                    <MoneyText amount={props.meta.data?.files[0]?.rate} currency={getCurrency(props.meta.data?.files[0]?.currency)} />
                                ) : (
                                    `${translate(
                                        `file.edit.form.finance.rates.type.${props.meta.data?.files[0]?.fileRateType
                                            .toLocaleLowerCase()
                                            .replaceAll('_', '')}`,
                                    )}, ${getCurrencyName(props.meta.data?.files[0]?.currency)} (${getCurrency(props.meta.data?.files[0]?.currency).symbol})`
                                )}
                            </Field>
                            <Field label={translate('invoiceapproval.times.rate')} orientation={props.headerOrientation}>
                                <MoneyText alpha={props.selectedTimes[0]?.rate.currency || 'EUR'} amount={totalTimeRate} />
                            </Field>
                            <Field label={translate('invoiceapproval.travels.rate')} orientation={props.headerOrientation}>
                                <MoneyText alpha={props.selectedTimes[0]?.rate.currency || 'EUR'} amount={totalTravelRate} />
                            </Field>
                            <Field label={translate('invoiceapproval.disbursements.rate')} orientation={props.headerOrientation}>
                                <MoneyText alpha={props.selectedTimes[0]?.rate.currency || 'EUR'} amount={totalDisbursementRate} />
                            </Field>
                            <Field label={translate('invoiceapproval.fixedprices.rate')} orientation={props.headerOrientation}>
                                <MoneyText alpha={props.selectedTimes[0]?.rate.currency || 'EUR'} amount={totalFixedPriceRate} />
                            </Field>
                            <Field label={translate('invoiceapproval.description')} orientation={props.headerOrientation}>
                                <Text>{props.meta.data?.files?.[0].description ?? '-'}</Text>
                            </Field>
                            <Field label={translate('invoiceapproval.notification')} orientation={props.headerOrientation}>
                                <Text>{props.meta.data?.files?.[0].notification ?? '-'}</Text>
                            </Field>
                        </>
                    )}
                </div>

                <div>
                    {props.meta.loaded && (
                        <>
                            <FormikComboboxField<InvoiceApprovalMutationDto, SubComboboxProps<FilePartyMetaDataDto>>
                                required
                                labelKey={'invoiceapproval.client.label'}
                                fieldName={'clientId'}
                                orientation={props.headerOrientation}
                                component={FilePartyCombobox}
                                componentProps={{
                                    entities: props.meta.data?.fileParties || [],
                                }}
                            />
                            <FormikComboboxField<InvoiceApprovalMutationDto, SubComboboxProps<FilePartyMetaDataDto>>
                                required
                                labelKey={'invoiceapproval.debtor.label'}
                                fieldName={'debtorId'}
                                orientation={props.headerOrientation}
                                component={FilePartyCombobox}
                                componentProps={{
                                    entities: props.meta.data?.fileParties || [],
                                }}
                            />
                            <FormikInputField<InvoiceApprovalMutationDto>
                                labelKey={'invoiceapproval.debtor.email'}
                                fieldName={'invoiceEmail'}
                                orientation={props.headerOrientation}
                            />
                            <FormikInputField<InvoiceApprovalMutationDto>
                                required={values.invoiceReferenceRequired}
                                labelKey={'invoiceapproval.debtor.reference'}
                                fieldName={'invoiceReference'}
                                orientation={props.headerOrientation}
                            />
                            {props.approval?.invoice && (
                                <>
                                    <Field label={translate('invoiceapproval.invoice.label')} orientation={props.headerOrientation}>
                                        <Link onClick={() => history.push(`/invoice/${props.approval.invoice.id}/edit`)}>
                                            {props.approval.invoice.number}
                                        </Link>
                                    </Field>
                                    <Field label={translate('invoiceapproval.invoice.pdf')} orientation={props.headerOrientation}>
                                        <StealthLink id={props.approval.invoice.pdfId} destination={Destination.Documents}>
                                            {translate('invoiceapproval.invoice.action')} <OpenIcon />
                                        </StealthLink>
                                    </Field>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            <NewCardFooter {...props} headerIsDirty={headerIsDirty} />
            {values.id && <EditCardFooter {...props} headerIsDirty={headerIsDirty} />}
        </Card>
    )
}
